import { useState } from "react";
import type { AnalyteGroup, AnalyteGroupRow } from "@probetalab/contracts";
import { get } from "../lib/api.js";
import { errorMessage, useDebounced, useQuery } from "../lib/useQuery.js";
import { Alert, Button, Chip, Empty, Loading, SearchInput } from "./ui.js";

const KIND: Record<string, string> = { numeric: "Numérico", text: "Texto", reference: "Opciones", subtitle: "Subtítulo", document: "Documento" };

/** The analyte list of Catálogo → Analitos, one query per typed search (every word must match). */
export function useAnalyteSearch(q: string, limit = 50, includeDeleted = false) {
  const dq = useDebounced(q.trim());
  return useQuery(() => get("catalogueAnalytes", { query: { q: dq || undefined, limit, include_deleted: includeDeleted || undefined } }), [dq, limit, includeDeleted]);
}

/** "BH · Biometría hemática, EGO · …" for the studies of one clave. */
export function studiesText(studies: { clave: string }[], max = 4): string {
  const shown = studies.slice(0, max).map((s) => s.clave);
  return studies.length > max ? `${shown.join(", ")} y ${studies.length - max} más` : shown.join(", ");
}

/**
 * Pick an analyte that already exists in another study (new-study and study editors, pilot
 * request of 2026-11-03): its definition is copied into the study as a new row with the same
 * clave, so Catálogo → Analitos can later edit both together. When the studies hold different
 * versions of it, the chemist chooses which one to copy.
 */
export function AnalytePicker({ exclude, onPick, onCancel }: { exclude: string[]; onPick: (row: AnalyteGroupRow["analyte"]) => void; onCancel: () => void }) {
  const [q, setQ] = useState("");
  const list = useAnalyteSearch(q, 30);
  const [group, setGroup] = useState<{ code: string; rows: AnalyteGroupRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const taken = new Set(exclude.map((c) => c.toUpperCase()));

  async function open(g: AnalyteGroup) {
    setError(null);
    try {
      const d = await get("catalogueAnalyteGroup", { query: { code: g.code } });
      if (g.variant_count === 1 || d.rows.length === 1) return onPick(d.rows[0]!.analyte);
      setGroup(d);
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <section className="card stack" style={{ padding: 14, gap: 10 }} aria-label="Analito de otro estudio">
      <div className="row between">
        <strong>{group ? `¿Qué versión de ${group.code} copias?` : "Agregar un analito que ya existe"}</strong>
        <Button size="sm" variant="ghost" onClick={group ? () => setGroup(null) : onCancel}>
          {group ? "Volver" : "Cancelar"}
        </Button>
      </div>
      {error && <Alert tone="red">{error}</Alert>}
      {group ? (
        <div className="stack" style={{ gap: 6 }}>
          {group.rows.map((r) => (
            <button key={r.analyte.id} type="button" className="list-row clickable" style={{ minHeight: 52, textAlign: "left" }} onClick={() => onPick(r.analyte)}>
              <span className="mono muted" style={{ width: 90 }}>
                {r.study.clave}
              </span>
              <span className="grow">
                {r.analyte.name}
                {r.analyte.unit ? ` · ${r.analyte.unit}` : ""}
                {r.analyte.options?.length ? <span className="small faint"> · {r.analyte.options.join(" / ")}</span> : null}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <>
          <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, clave o unidad…" aria-label="Buscar analito existente" autoFocus />
          {list.loading && !list.data ? (
            <Loading />
          ) : !list.data?.items.length ? (
            <Empty icon="search" title="Sin analitos" hint="Prueba con otra palabra." />
          ) : (
            <div className="stack" style={{ gap: 4, maxHeight: 320, overflowY: "auto" }}>
              {list.data.items.map((g) => {
                const dup = taken.has(g.code.toUpperCase());
                return (
                  <button key={g.code} type="button" className="list-row clickable" style={{ minHeight: 52, textAlign: "left" }} disabled={dup} onClick={() => void open(g)}>
                    <span className="mono muted" style={{ width: 90 }}>
                      {g.code}
                    </span>
                    <span className="grow">
                      {g.name}
                      {g.unit ? <span className="muted"> · {g.unit}</span> : null}
                      <span className="small faint"> · {KIND[g.kind]} · {studiesText(g.studies)}</span>
                    </span>
                    {dup && <Chip>Ya está en este estudio</Chip>}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}
